import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, TextField, Card, CardContent,
  CircularProgress, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import SailingIcon from '@mui/icons-material/Sailing';
import HotelIcon from '@mui/icons-material/Hotel';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import LuggageIcon from '@mui/icons-material/Luggage';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SurfingIcon from '@mui/icons-material/Surfing';
import BusinessIcon from '@mui/icons-material/Business';
import { StyledDialog, inputStyles } from './common';
import { cardStyle as baseCardStyle } from './common/naraCards';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

const ICON_MAP = {
  SailingIcon: <SailingIcon />,
  HotelIcon: <HotelIcon />,
  DirectionsBusIcon: <DirectionsBusIcon />,
  LuggageIcon: <LuggageIcon />,
  RestaurantIcon: <RestaurantIcon />,
  SurfingIcon: <SurfingIcon />,
  BusinessIcon: <BusinessIcon />,
  CategoryIcon: <CategoryIcon />,
};

const ICON_OPTIONS = [
  { value: 'SailingIcon', label: 'Navegación' },
  { value: 'HotelIcon', label: 'Hotel' },
  { value: 'DirectionsBusIcon', label: 'Transporte' },
  { value: 'LuggageIcon', label: 'Equipaje/Viaje' },
  { value: 'RestaurantIcon', label: 'Restaurante' },
  { value: 'SurfingIcon', label: 'Aventura' },
  { value: 'BusinessIcon', label: 'Empresa' },
  { value: 'CategoryIcon', label: 'Categoría' },
];

const cardStyle = { ...baseCardStyle };

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export default function AdminTiposEmpresa() {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ nombre: '', descripcion: '', icono: 'BusinessIcon', orden: '0' });
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/turismo/tipos-empresa?solo_activos=false`, { headers: getAuthHeaders() });
      setTipos(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleOpen = (item = null) => {
    setEditItem(item);
    setError('');
    setForm(item
      ? { nombre: item.nombre, descripcion: item.descripcion || '', icono: item.icono || 'BusinessIcon', orden: String(item.orden || 0) }
      : { nombre: '', descripcion: '', icono: 'BusinessIcon', orden: '0' }
    );
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setError('');
    const body = { ...form, orden: parseInt(form.orden) || 0 };
    const url = editItem
      ? `${API_BASE}/turismo/tipos-empresa/${editItem.id}`
      : `${API_BASE}/turismo/tipos-empresa`;
    const res = await fetch(url, {
      method: editItem ? 'PUT' : 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      const data = await res.json();
      setError(data.detail || 'Error al guardar');
      return;
    }
    setDialogOpen(false);
    cargar();
  };

  const handleDelete = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar el tipo "${nombre}"?`)) return;
    const res = await fetch(`${API_BASE}/turismo/tipos-empresa/${id}`, {
      method: 'DELETE', headers: getAuthHeaders()
    });
    if (!res.ok) {
      const data = await res.json();
      alert(data.detail || 'Error al eliminar');
      return;
    }
    cargar();
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#134e4a', mb: 0.5 }}>
          Tipos de Empresa Turística
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#64748b' }}>
          Define los tipos de empresa que operan en la plataforma. Cada empresa se clasifica en un tipo al registrarse.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
          sx={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)', borderRadius: 3 }}
        >
          Nuevo Tipo
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {tipos.map(t => (
          <Card key={t.id} sx={cardStyle}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{
                  width: 44, height: 44, borderRadius: 3,
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', boxShadow: '0 8px 20px rgba(139, 92, 246, 0.4)',
                  '& svg': { fontSize: 22 }
                }}>
                  {ICON_MAP[t.icono] || <CategoryIcon />}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#134e4a' }}>{t.nombre}</Typography>
                  <Typography sx={{ fontSize: 11, color: '#64748b' }}>Orden: {t.orden}</Typography>
                </Box>
                <Box>
                  <IconButton size="small" onClick={() => handleOpen(t)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => handleDelete(t.id, t.nombre)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                </Box>
              </Box>
              {t.descripcion && (
                <Typography sx={{ fontSize: 12, color: '#475569', mb: 1 }}>{t.descripcion}</Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {!t.activo && <Chip label="Inactivo" size="small" color="warning" sx={{ fontSize: 11 }} />}
                <Chip label={t.icono || 'sin icono'} size="small" variant="outlined" sx={{ fontSize: 10 }} />
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <StyledDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editItem ? 'Editar Tipo de Empresa' : 'Nuevo Tipo de Empresa'}
        icon={<CategoryIcon />}
        variant="compact"
        maxWidth="xs"
      >
        <StyledDialog.Body compact>
          <TextField
            sx={inputStyles}
            label="Nombre"
            fullWidth
            value={form.nombre}
            onChange={e => setForm({ ...form, nombre: e.target.value })}
            placeholder="Ej: Operadores de Cruceros"
          />
          <TextField
            sx={inputStyles}
            label="Descripción"
            fullWidth
            multiline
            rows={2}
            value={form.descripcion}
            onChange={e => setForm({ ...form, descripcion: e.target.value })}
          />
          <FormControl fullWidth sx={inputStyles}>
            <InputLabel>Icono</InputLabel>
            <Select
              value={form.icono}
              label="Icono"
              onChange={e => setForm({ ...form, icono: e.target.value })}
            >
              {ICON_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value} sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {ICON_MAP[opt.value]} {opt.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            sx={inputStyles}
            label="Orden de visualización"
            fullWidth
            type="number"
            value={form.orden}
            onChange={e => setForm({ ...form, orden: e.target.value })}
          />
          {error && (
            <Typography sx={{ color: '#dc2626', fontSize: 13, mt: 1 }}>{error}</Typography>
          )}
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setDialogOpen(false)} />
          <StyledDialog.PrimaryButton fullWidth onClick={handleSave} disabled={!form.nombre}>
            {editItem ? 'Actualizar' : 'Crear'}
          </StyledDialog.PrimaryButton>
        </StyledDialog.CompactActions>
      </StyledDialog>
    </Box>
  );
}
